import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Deck & Patio's full question set (Phase 11). `deck_patio` is a single
 * project type covering two genuinely different builds - an elevated or
 * ground-level wood/composite deck, and a ground-level poured/paved
 * patio - so the first question (`deck_patio_kind`) picks which one, and
 * several later questions are gated on that answer via `displayIf`. Per
 * the Phase 11 brief's explicit "do not use interior renovation
 * assumptions": there is no reused kitchen/basement sqft category here -
 * every category below maps to its own deck/patio-appropriate pricing
 * unit (sqft for surface/base construction, linear ft for railings, item
 * for stairs/fireplace-style fixtures, allowance for roof/cover and
 * mechanical-style categories, percentage for demolition) - see
 * `rules/deck-patio.ts` and `docs/deck-patio-estimator.md`.
 *
 * Renders through the same generic `QuestionRenderer`/
 * `ProjectSpecificQuestions` pair every project type uses - no dedicated
 * step UI is needed even for size, unlike kitchen/basement, since the
 * brief didn't ask for bucketed size ranges here; the existing generic
 * `room_size` slider (packages/config/src/questions/common-questions.ts)
 * already asks "approximately how large is the space," which is exactly
 * the brief's "approximate size" input.
 */
export const deckPatioQuestions: QuestionDefinition[] = [
  {
    id: "deck_patio_kind",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Are you building a deck or a patio?",
    helpText: "A deck is a raised or ground-level wood/composite structure; a patio is a ground-level poured or paved surface.",
    required: true,
    options: [
      { id: "deck", label: "Deck", description: "Wood or composite structure, ground-level or elevated." },
      { id: "patio", label: "Patio", description: "Ground-level concrete, paver, or stone surface." }
    ],
    order: 180
  },
  {
    id: "deck_patio_construction_type",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Is this new construction or a replacement?",
    helpText: "A replacement usually means removing an existing deck or patio before rebuilding.",
    required: true,
    options: [
      { id: "new_construction", label: "New construction", description: "No existing deck/patio in this location." },
      { id: "replacement", label: "Replacement", description: "Removing and rebuilding an existing deck or patio." }
    ],
    order: 185
  },
  {
    id: "deck_patio_material",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "What decking material are you planning?",
    required: false,
    displayIf: [{ questionId: "deck_patio_kind", equalsAnyOf: ["deck"] }],
    options: [
      { id: "pressure_treated", label: "Pressure-treated wood", description: "The most common, most affordable decking material." },
      { id: "cedar", label: "Cedar", description: "A natural wood upgrade with better weathering and appearance than pressure-treated." },
      { id: "composite", label: "Composite", description: "Low-maintenance synthetic/wood-fibre boards." },
      { id: "premium_custom", label: "Premium / custom", description: "High-end composite, PVC, or exotic hardwood decking." }
    ],
    order: 200
  },
  {
    id: "deck_patio_patio_material",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "What patio surface material are you planning?",
    required: false,
    displayIf: [{ questionId: "deck_patio_kind", equalsAnyOf: ["patio"] }],
    options: [
      { id: "concrete", label: "Poured concrete", description: "The most common, most affordable patio surface." },
      { id: "pavers", label: "Pavers", description: "Interlocking concrete or brick pavers." },
      { id: "natural_stone", label: "Natural stone", description: "Flagstone or similar natural stone paving." },
      { id: "premium_custom", label: "Premium / custom", description: "High-end stone, stamped/decorative concrete, or custom patterns." }
    ],
    order: 201
  },
  {
    id: "deck_patio_elevation",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Ground-level or elevated?",
    helpText: "Elevated decks need additional framing, footings, and (usually) railings - see the note on your results.",
    required: false,
    displayIf: [{ questionId: "deck_patio_kind", equalsAnyOf: ["deck"] }],
    options: [
      { id: "ground_level", label: "Ground-level", description: "At or near grade - minimal framing height." },
      { id: "elevated", label: "Elevated", description: "Raised significantly above grade (e.g. off a second-storey walkout)." }
    ],
    order: 205
  },
  {
    id: "deck_patio_stairs",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Will there be stairs?",
    required: false,
    options: [
      { id: "none", label: "No stairs" },
      { id: "one_set", label: "One set" },
      { id: "two_or_more_sets", label: "Two or more sets" }
    ],
    order: 210
  },
  {
    id: "deck_patio_railings",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Will there be railings?",
    helpText: "Most Canadian building codes require a guard/railing above a certain height - see the note on your results if this applies to you.",
    required: false,
    options: [
      { id: "none", label: "No railings" },
      { id: "standard", label: "Standard", description: "Wood or metal baluster railing." },
      { id: "premium_glass_cable", label: "Premium (glass / cable)", description: "Glass panel or stainless cable railing system." }
    ],
    order: 215
  },
  {
    id: "deck_patio_levels",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "How many levels/tiers?",
    required: false,
    options: [
      { id: "single_level", label: "Single level" },
      { id: "two_levels", label: "Two levels" },
      { id: "three_plus_levels", label: "Three or more levels" }
    ],
    order: 220
  },
  {
    id: "deck_patio_roof_cover",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "Will there be a roof or cover?",
    required: false,
    options: [
      { id: "none", label: "No roof/cover" },
      { id: "pergola", label: "Pergola", description: "Open-slat overhead structure, partial shade." },
      { id: "solid_roof", label: "Solid roof/cover", description: "Fully enclosed roof structure - full sun/rain protection." }
    ],
    order: 225
  },
  {
    id: "deck_patio_structural_complexity",
    projectTypes: ["deck_patio"],
    inputType: "single_select",
    prompt: "How structurally complex is the build?",
    helpText: "Consider height, ground slope, long unsupported spans, or attaching to an existing structure.",
    required: false,
    options: [
      { id: "standard", label: "Standard", description: "Typical framing, flat/near-flat site." },
      { id: "some_complexity", label: "Some complexity", description: "Sloped site, longer spans, or an awkward attachment point." },
      { id: "significant_engineering_required", label: "Significant - likely needs engineering", description: "Substantial height, long spans, or unusual structural conditions." }
    ],
    order: 230
  }
];
